import { saveChangeHistoryUrl } from "../redux/constants";
import { putWithAuth } from "../redux/makeFetch";
import { log } from "./index";

const DIRTY_FIELDS_IGNORE_LIST = ["psOpenId", "protocolNumber"];

export const updateRequestHistory = async (
  dirtyFields,
  requestData,
  tabUpdated,
  userAction
) => {
  try {
    const changes = Object.keys(dirtyFields).filter(
      (field) => !DIRTY_FIELDS_IGNORE_LIST.includes(field)
    );
    const requestObject = {
      action: "UPDATE",
      audId: 0,
      comments: "",
      dbId: requestData.requestNumber,
      fromState: requestData.status || "NEW",
      objKey: requestData.requestNumber,
      objType: "PROTSETUP",
      objValue: {
        requestId: requestData.requestNumber,
        tabName: tabUpdated,
        updatedFields: changes.join(", "),
        userAction
      },
      toState: "UPDATED"
    };

    await putWithAuth(saveChangeHistoryUrl, requestObject);
  } catch (error) {
    log("Error updating request history:", error);
  }
};

/**
 * Higher-order function that wraps form submission handlers to automatically
 * call updateRequestHistory after successful saves
 *
 * @param {Function} originalOnSubmit - The original form submission handler
 * @param {Object} defaultValues - The original form values for comparison
 * @param {string} tabUpdated - The tab that was updated
 * @param {string} userAction - The user action (Initial Save, Submit, Signoff)
 * @returns {Function} - Enhanced submission handler with history tracking
 */
export const withRequestHistory = (
  originalOnSubmit,
  dirtyFields,
  tabUpdated,
  userAction = "UPDATE"
) => {
  return async (data, ...args) => {
    // Call the original onSubmit handler
    const result = await originalOnSubmit(data, ...args);
    // If successful, update request history
    if (result !== false) {
      await updateRequestHistory(dirtyFields, data, tabUpdated, userAction);
    }

    return result;
  };
};

/**
 * React Hook version for easier integration with useForm
 *
 * @param {Function} originalOnSubmit - The original form submission handler
 * @param {Object} defaultValues - The original form values for comparison
 * @param {string} tabUpdated - The tab that was updated(
 * Protocol Information,
 * OPEN Setup,
 * Rave Setup,
 * Signoff: Submitted,
 * Signoff: UAT Setup,
 * Signoff: Group Testing,
 * Signoff: CTSU Testing,
 * Signoff: Pending Signoff,
 * Signoff: Completed)
 * @param {string} userAction - The user action (Initial Save, Updates, Submit, Signoff)
 * @returns {Function} - Enhanced submission handler with history tracking
 */
export const useRequestHistorySubmit = (
  originalOnSubmit,
  dirtyFields,
  tabUpdated,
  userAction = "Initial Save"
) => {
  return async (data, ...args) => {
    let result;

    try {
      // Call the original onSubmit handler
      result = await originalOnSubmit(data, ...args);
    } catch (error) {
      log("Error in originalOnSubmit:", error);
      throw error;
    }
    if (result !== false) {
      await updateRequestHistory(dirtyFields, data, tabUpdated, userAction);
    }
    return result;
  };
};
